import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  deleteAislePhoto,
  fetchAislePhotoObjectUrl,
  listAislePhotos,
  uploadAislePhoto,
  type AislePhotoRow,
} from "../api/rest";
import { ROLE_RANK, useSiteStore } from "../stores/siteStore";
import slot from "./SlotPhoto.module.css";
import styles from "./AislePhoto.module.css";

/**
 * The aisle's picture on the aisle card (ADR 398) — what the place looks like, next to
 * the numbers about it. Same shape as the pallet photo on the inspector (ADR 272): one
 * frame, a count when there are more, a click opens the set.
 *
 * It is reference, not evidence, and the differences follow from that: no source badge
 * and no "use as evidence" (there is nothing to prove), and a picture hung on the wrong
 * aisle can be deleted, which a pallet's photo cannot.
 *
 * Bytes are fetched with the bearer token and wrapped in an object URL for the reason
 * SlotPhoto gives (ADR 204); the URL is revoked when the photo changes or on unmount.
 */
function useAislePhotoUrl(siteId: string, warehouseId: string, id: string | null) {
  const key = id && siteId && warehouseId ? `${siteId}/${warehouseId}/${id}` : null;
  const [result, setResult] = useState<{ key: string; url: string | null; failed: boolean } | null>(null);

  useEffect(() => {
    if (!key || !id) return;
    let revoked = false;
    let created: string | null = null;
    fetchAislePhotoObjectUrl(siteId, warehouseId, id)
      .then((u) => {
        if (revoked) { URL.revokeObjectURL(u); return; }
        created = u;
        setResult({ key, url: u, failed: false });
      })
      .catch(() => { if (!revoked) setResult({ key, url: null, failed: true }); });
    return () => {
      revoked = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [key, siteId, warehouseId, id]);

  const current = result && result.key === key ? result : null;
  return { url: current?.url ?? null, failed: current?.failed ?? false };
}

function Thumb({
  siteId, warehouseId, photo, label, onOpen,
}: { siteId: string; warehouseId: string; photo: AislePhotoRow; label: string; onOpen: () => void }) {
  const { t } = useTranslation();
  const { url, failed } = useAislePhotoUrl(siteId, warehouseId, photo.id);
  return (
    <button type="button" className={slot.thumbBtn} onClick={onOpen} aria-label={label}>
      {url
        ? <img className={slot.thumbImg} src={url} alt="" />
        : <span className={slot.thumbPending}>{failed ? t("aislePhoto.failed") : t("common.loading")}</span>}
    </button>
  );
}

function Lightbox({
  siteId, warehouseId, aisle, photos, index, canEdit, onIndex, onClose,
}: {
  siteId: string; warehouseId: string; aisle: string; photos: AislePhotoRow[];
  index: number; canEdit: boolean; onIndex: (i: number) => void; onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const closeRef = useRef<HTMLButtonElement>(null);
  const photo = photos[index];
  const { url, failed } = useAislePhotoUrl(siteId, warehouseId, photo?.id ?? null);

  useEffect(() => { closeRef.current?.focus(); }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.stopPropagation(); onClose(); }
      if (e.key === "ArrowRight" && index < photos.length - 1) onIndex(index + 1);
      if (e.key === "ArrowLeft" && index > 0) onIndex(index - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, photos.length, onIndex, onClose]);

  const remove = useMutation({
    mutationFn: () => deleteAislePhoto(siteId, warehouseId, photo.id),
    onSuccess: () => {
      // The set shrinks under the open index: close rather than show a neighbour the
      // user did not pick.
      onClose();
      void qc.invalidateQueries({ queryKey: ["aisle-photos", siteId, warehouseId] });
    },
  });

  if (!photo) return null;
  const when = photo.takenAt
    ? new Date(photo.takenAt).toLocaleString(i18n.language)
    : t("aislePhoto.sentAt", { when: new Date(photo.uploadedAt).toLocaleString(i18n.language) });

  return createPortal(
    <div className={slot.backdrop} role="dialog" aria-modal="true" aria-label={t("aislePhoto.dialog", { aisle })}>
      <div className={slot.lightHead}>
        <div>
          <div className={slot.lightAddress}>{t("aislePhoto.dialog", { aisle })}</div>
          <div className={slot.lightMeta}>
            {when}
            {photo.widthPx && photo.heightPx ? ` · ${photo.widthPx}×${photo.heightPx}` : ""}
            {` · ${Math.round(photo.byteSize / 1024)} kB`}
            {photo.note ? ` · ${photo.note}` : ""}
          </div>
        </div>
        <button ref={closeRef} type="button" className={slot.closeBtn} onClick={onClose}>
          {t("common.close")}
        </button>
      </div>
      <div className={slot.lightBody}>
        {url
          ? <img className={slot.lightImg} src={url} alt={t("aislePhoto.alt", { aisle })} />
          : <span className={slot.chosenNote}>{failed ? t("aislePhoto.failed") : t("common.loading")}</span>}
      </div>
      <div className={slot.lightFoot}>
        <div className={slot.filmstrip}>
          {photos.map((p, i) => (
            <Thumb key={p.id} siteId={siteId} warehouseId={warehouseId} photo={p}
                   label={t("aislePhoto.open", { aisle })} onOpen={() => onIndex(i)} />
          ))}
        </div>
        {canEdit && (
          <div className={slot.footActions}>
            <button type="button" className={styles.deleteBtn} disabled={remove.isPending}
                    onClick={() => { if (window.confirm(t("aislePhoto.deleteConfirm"))) remove.mutate(); }}>
              {remove.isPending ? t("aislePhoto.deleting") : t("aislePhoto.delete")}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

function Hero({
  siteId, warehouseId, photo, count, aisle, onOpen,
}: { siteId: string; warehouseId: string; photo: AislePhotoRow; count: number; aisle: string; onOpen: () => void }) {
  const { t } = useTranslation();
  const { url, failed } = useAislePhotoUrl(siteId, warehouseId, photo.id);
  return (
    <button type="button" className={styles.hero} onClick={onOpen} aria-label={t("aislePhoto.open", { aisle })}>
      {url
        ? <img className={styles.heroImg} src={url} alt="" />
        : <span className={styles.heroPending}>{failed ? t("aislePhoto.failed") : t("common.loading")}</span>}
      {count > 1 && (
        <span className={slot.heroCount} aria-label={t("aislePhoto.countAria", { count })}>{count}</span>
      )}
    </button>
  );
}

/**
 * The photo block of the aisle card. Renders nothing when the aisle has no picture and
 * the viewer cannot add one — the same rule as the pallet strip (ADR 296): hide a
 * control whose data is absent, not an action whose target is on screen.
 */
export function AislePhotoBlock({
  siteId, warehouseId, nodeId, aisle,
}: { siteId: string; warehouseId: string; nodeId: string; aisle: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState<number | null>(null);
  // Mirrors the server (assertOperateAccess, ADR 395): the people who walk the floor.
  const canEdit = useSiteStore((st) => st.isGlobalAdmin || st.roleRank >= ROLE_RANK.supervisor);

  const { data } = useQuery({
    queryKey: ["aisle-photos", siteId, warehouseId, nodeId],
    queryFn: () => listAislePhotos(siteId, warehouseId, nodeId),
    enabled: !!siteId && !!warehouseId && !!nodeId,
    staleTime: 60_000,
  });
  const photos = data ?? [];

  const upload = useMutation({
    mutationFn: (file: File) => uploadAislePhoto(siteId, warehouseId, { file, nodeId }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["aisle-photos", siteId, warehouseId, nodeId] }); },
  });

  if (!photos.length && !canEdit) return null;

  return (
    <div className={styles.block}>
      <span className={styles.title}>{t("aislePhoto.title")}</span>
      {photos.length > 0 && (
        <Hero siteId={siteId} warehouseId={warehouseId} photo={photos[0]} count={photos.length}
              aisle={aisle} onOpen={() => setOpen(0)} />
      )}
      {canEdit && (
        <>
          <label className={slot.uploadBtn}>
            {upload.isPending ? t("aislePhoto.uploading")
                              : t(photos.length ? "aislePhoto.addAnother" : "aislePhoto.add")}
            <input
              ref={inputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              capture="environment"
              className={slot.uploadInput}
              disabled={upload.isPending}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (file) upload.mutate(file);
              }}
            />
          </label>
          {upload.isError && (
            <span className={slot.uploadError}>
              {t("aislePhoto.uploadFailed", { msg: (upload.error as Error).message })}
            </span>
          )}
        </>
      )}
      {open != null && photos.length > 0 && (
        <Lightbox siteId={siteId} warehouseId={warehouseId} aisle={aisle} photos={photos}
                  index={Math.min(open, photos.length - 1)} canEdit={canEdit}
                  onIndex={setOpen} onClose={() => setOpen(null)} />
      )}
    </div>
  );
}
